import React, { useState } from 'react';
import { 
  Key, ShieldCheck, Copy, Check, ExternalLink, 
  LogIn, Plus, Trash2, Database, Server, 
  Sparkles, Download, Globe, MessageSquare, AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useBusinessStore } from '../store/useBusinessStore';
import { useAuthStore } from '../store/useAuthStore';
import { DEMO_ACCOUNTS, getStoredAdminEmails, saveStoredAdminEmails } from '../utils/adminConstants';

export default function AdminCredentialsVault() {
  const navigate = useNavigate();
  const { masterDb, setActiveBusinessId } = useBusinessStore();
  const { setAuth } = useAuthStore();

  const [adminEmails, setAdminEmails] = useState(() => getStoredAdminEmails());
  const [newAdminEmailInput, setNewAdminEmailInput] = useState('');
  const [copiedKey, setCopiedKey] = useState(null);

  const copyToClipboard = (text, keyLabel) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(keyLabel);
    toast.success(`Copiado: ${keyLabel}`);
    setTimeout(() => setCopiedKey(null), 2500);
  };

  const handleAddAdminEmail = () => {
    const clean = newAdminEmailInput.trim().toLowerCase();
    if (!clean || !clean.includes('@')) {
      toast.error("Ingresa un correo electrónico válido.");
      return;
    }
    if (adminEmails.includes(clean)) {
      toast.error("Este correo ya está autorizado como Administrador Master.");
      return;
    }
    const updated = [...adminEmails, clean];
    setAdminEmails(updated);
    saveStoredAdminEmails(updated);
    setNewAdminEmailInput('');
    toast.success(`¡Correo "${clean}" autorizado como Master!`);
  };

  const handleRemoveAdminEmail = (emailToRemove) => {
    if (adminEmails.length <= 1) {
      toast.error("Debe existir al menos un Administrador Master.");
      return;
    }
    if (window.confirm(`¿Estás seguro de remover los permisos de Administrador Master a "${emailToRemove}"?`)) {
      const updated = adminEmails.filter(e => e !== emailToRemove);
      setAdminEmails(updated);
      saveStoredAdminEmails(updated);
      toast.success(`Permisos de Administrador removidos para ${emailToRemove}`);
    }
  };

  const handleImpersonateDemo = (demo) => {
    const matchedBiz = (masterDb || []).find(b => 
      String(b.id) === demo.id || 
      demo.legacyIds.includes(String(b.id)) ||
      (b.slug && b.slug.toLowerCase() === demo.slug.toLowerCase())
    );

    const targetId = matchedBiz ? String(matchedBiz.id) : demo.id;
    setActiveBusinessId(targetId);
    setAuth(true, demo.demoEmail, 'client');
    toast.success(`⚡ Ingresando a la Consola de: ${demo.name}`);
    navigate('/v2/dashboard');
  };

  const handleDownloadCredentialsDoc = () => {
    const content = `# 🔐 BÓVEDA DE CREDENCIALES & CUENTAS DEMO - KEYLA CRM
Fecha de exportación: ${new Date().toLocaleString()}

## 👑 ADMINISTRADORES MASTER AUTORIZADOS
${adminEmails.map(e => `- ${e}`).join('\n')}

---

## 🎮 CUENTAS DEMO PRECONFIGURADAS
${DEMO_ACCOUNTS.map(d => {
  const matchedBiz = (masterDb || []).find(b => String(b.id) === d.id || d.legacyIds.includes(String(b.id)));
  const realEmail = matchedBiz?.google_calendar_id;
  return `### ${d.icon} ${d.name} (${d.niche})
- ID de Negocio: \`${d.id}\`
- Correo de Login Demo: \`${d.demoEmail}\`
- Contraseña Demo: \`${d.demoPassword}\`
- Correo Real de Notificaciones / GCalendar: \`${realEmail || '(Sin configurar - usa correo demo)'}\`
- Slug Minisitio: \`${d.slug}\`
- URL Minisitio: https://keyla.seryiads.com/app/${d.slug}
- Características activas: ${d.features.join(', ')}
`;
}).join('\n---\n')}

---

## 🛠️ INFRAESTRUCTURA & SERVICIOS
- Supabase REST URL: https://supabase.seryiads.com
- Coolify Admin: https://coolify.seryiads.com
- n8n Automation Engine: https://flow.seryiads.com
- PWA Producción: https://keyla.seryiads.com
- Landing Page: https://seryiads.com
`;

    const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `CREDENCIALES_KEYLACRM_${Date.now()}.md`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Documento de credenciales descargado");
  };

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-gradient-to-r from-seryi-bg via-seryi-bg to-indigo-950/30 border border-seryi-bg-light p-6 md:p-8 rounded-3xl">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 px-3 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
              <Key size={12} /> Bóveda de Seguridad & Accesos
            </span>
          </div>
          <h2 className="text-2xl md:text-3xl font-black text-seryi-heading tracking-tight">
            Credenciales, Demos & SuperAdmins
          </h2>
          <p className="text-xs md:text-sm text-seryi-text max-w-2xl">
            Gestiona los correos con acceso Master, consulta las claves de las cuentas demo para clientes y accede con 1-clic a cualquier entorno de prueba.
          </p>
        </div>

        <button
          onClick={handleDownloadCredentialsDoc}
          className="bg-seryi-bg-dark hover:bg-seryi-bg-light border border-seryi-bg-light text-seryi-heading font-bold px-4 py-3 rounded-2xl text-xs flex items-center gap-2 transition-all shadow-lg active:scale-95 shrink-0"
        >
          <Download size={16} className="text-seryi-green" />
          <span>Descargar Respaldo (.md)</span>
        </button>
      </div>

      {/* 1. SECCIÓN: CUENTAS DEMO PRECONFIGURADAS */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="text-seryi-green" size={20} />
            <h3 className="text-lg font-black text-seryi-heading">Cuentas Demo para Presentaciones y Ventas</h3>
          </div>
          <span className="text-[11px] text-seryi-text font-medium">Contraseña universal para demos: <code className="text-seryi-green bg-seryi-bg-dark px-2 py-0.5 rounded-lg border border-seryi-bg-light">demo</code></span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {DEMO_ACCOUNTS.map((demo) => {
            const matchedBiz = (masterDb || []).find(b => 
              String(b.id) === demo.id || 
              demo.legacyIds.includes(String(b.id)) ||
              (b.slug && b.slug.toLowerCase() === demo.slug.toLowerCase())
            );
            const realCalendarEmail = matchedBiz?.google_calendar_id;
            const isUsingRealEmail = realCalendarEmail && realCalendarEmail !== demo.demoEmail;

            return (
              <div 
                key={demo.id} 
                className="bg-seryi-bg border border-seryi-bg-light hover:border-seryi-green/40 p-6 rounded-3xl flex flex-col justify-between space-y-6 transition-all group shadow-lg shadow-black/20"
              >
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-2xl bg-seryi-bg-dark border border-seryi-bg-light flex items-center justify-center text-2xl shadow-inner">
                        {demo.icon}
                      </div>
                      <div>
                        <h4 className="font-extrabold text-base text-seryi-heading group-hover:text-seryi-green transition-colors">
                          {demo.name}
                        </h4>
                        <p className="text-[11px] text-seryi-text font-semibold">{demo.niche}</p>
                      </div>
                    </div>
                  </div>

                  <p className="text-xs text-seryi-text leading-relaxed">
                    {demo.description}
                  </p>

                  {/* Tarjeta de Credenciales */}
                  <div className="bg-seryi-bg-dark/80 border border-seryi-bg-light rounded-2xl p-4 space-y-3">
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-[10px] uppercase font-bold text-seryi-text">Correo Demo:</span>
                      <div className="flex items-center gap-1.5 font-mono text-seryi-heading font-bold">
                        <span>{demo.demoEmail}</span>
                        <button
                          onClick={() => copyToClipboard(demo.demoEmail, `Correo ${demo.name}`)}
                          className="text-seryi-text hover:text-seryi-green p-1 transition-colors"
                          title="Copiar correo"
                        >
                          {copiedKey === `Correo ${demo.name}` ? <Check size={13} className="text-seryi-green" /> : <Copy size={13} />}
                        </button>
                      </div>
                    </div>

                    <div className="flex justify-between items-center text-xs">
                      <span className="text-[10px] uppercase font-bold text-seryi-text">Contraseña:</span>
                      <div className="flex items-center gap-1.5 font-mono text-emerald-400 font-bold">
                        <span>{demo.demoPassword}</span>
                        <button
                          onClick={() => copyToClipboard(demo.demoPassword, `Clave ${demo.name}`)}
                          className="text-seryi-text hover:text-seryi-green p-1 transition-colors"
                          title="Copiar contraseña"
                        >
                          {copiedKey === `Clave ${demo.name}` ? <Check size={13} className="text-seryi-green" /> : <Copy size={13} />}
                        </button>
                      </div>
                    </div>

                    {isUsingRealEmail && (
                      <div className="pt-2 border-t border-seryi-bg-light/60 flex flex-col gap-1">
                        <span className="text-[10px] uppercase font-bold text-indigo-400 flex items-center gap-1">
                          <CheckCircle size={10} /> Correo Real de Pruebas:
                        </span>
                        <span className="text-xs font-mono text-seryi-heading font-medium truncate bg-indigo-950/30 px-2 py-1 rounded-lg border border-indigo-800/40">
                          {realCalendarEmail}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Acciones de la Demo */}
                <div className="space-y-2 pt-2">
                  <button
                    onClick={() => handleImpersonateDemo(demo)}
                    className="w-full bg-seryi-green text-seryi-bg-dark hover:bg-seryi-green/80 font-bold py-3 rounded-2xl text-xs flex items-center justify-center gap-2 shadow-lg shadow-seryi-green/10 transition-all active:scale-98 cursor-pointer"
                  >
                    <LogIn size={15} />
                    <span>⚡ Entrar Directo (Impersonar)</span>
                  </button>

                  <a
                    href={`/app/${demo.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="w-full bg-seryi-bg-dark hover:bg-seryi-bg-light/50 border border-seryi-bg-light text-seryi-text hover:text-seryi-heading font-semibold py-2.5 rounded-2xl text-xs flex items-center justify-center gap-2 transition-all"
                  >
                    <Globe size={14} />
                    <span>Ver Minisitio Público</span>
                    <ExternalLink size={12} />
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 2. SECCIÓN: ADMINISTRADORES MASTER AUTORIZADOS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-seryi-bg border border-seryi-bg-light p-6 md:p-8 rounded-3xl space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-seryi-green/10 rounded-2xl flex items-center justify-center text-seryi-green">
              <ShieldCheck size={22} />
            </div>
            <div>
              <h3 className="text-base font-extrabold text-seryi-heading">Administradores Master Autorizados</h3>
              <p className="text-xs text-seryi-text">Correos con acceso total al panel de administración y control global.</p>
            </div>
          </div>

          <div className="flex gap-2">
            <input
              type="email"
              placeholder="nuevo.admin@ejemplo.com"
              value={newAdminEmailInput}
              onChange={(e) => setNewAdminEmailInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddAdminEmail()}
              className="flex-1 bg-seryi-bg-dark border border-seryi-bg-light rounded-2xl px-4 py-3 text-xs text-seryi-heading focus:outline-none focus:border-seryi-green transition-all placeholder:text-seryi-text"
            />
            <button
              onClick={handleAddAdminEmail}
              className="bg-seryi-green text-seryi-bg-dark hover:bg-seryi-green/80 font-bold px-5 py-3 rounded-2xl text-xs flex items-center gap-1.5 transition-all active:scale-95 shrink-0"
            >
              <Plus size={16} /> Autorizar
            </button>
          </div>

          <div className="space-y-2.5">
            {adminEmails.map((emailItem) => (
              <div 
                key={emailItem} 
                className="bg-seryi-bg-dark border border-seryi-bg-light/80 p-3.5 rounded-2xl flex items-center justify-between group hover:border-seryi-bg-light transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
                  <span className="text-xs font-mono font-bold text-seryi-heading">{emailItem}</span>
                </div>
                {adminEmails.length > 1 && (
                  <button
                    onClick={() => handleRemoveAdminEmail(emailItem)}
                    className="text-rose-400/70 hover:text-rose-400 hover:bg-rose-500/10 p-2 rounded-xl transition-all"
                    title={`Remover a ${emailItem}`}
                  >
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 3. SECCIÓN: SERVICIOS E INFRAESTRUCTURA */}
        <div className="bg-seryi-bg border border-seryi-bg-light p-6 md:p-8 rounded-3xl space-y-6 flex flex-col justify-between">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-500/10 rounded-2xl flex items-center justify-center text-indigo-400">
                <Server size={22} />
              </div>
              <div>
                <h3 className="text-base font-extrabold text-seryi-heading">Infraestructura & Endpoints Clave</h3>
                <p className="text-xs text-seryi-text">Servicios conectados para automatizaciones y almacenamiento.</p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="bg-seryi-bg-dark border border-seryi-bg-light p-3.5 rounded-2xl flex items-center justify-between text-xs">
                <div>
                  <span className="text-[10px] font-bold text-seryi-text uppercase block">Base de Datos (Supabase)</span>
                  <span className="font-mono text-seryi-heading font-medium">https://supabase.seryiads.com</span>
                </div>
                <button
                  onClick={() => copyToClipboard('https://supabase.seryiads.com', 'Supabase URL')}
                  className="text-seryi-text hover:text-seryi-green p-1.5 rounded-lg transition-colors"
                >
                  {copiedKey === 'Supabase URL' ? <Check size={14} className="text-seryi-green" /> : <Copy size={14} />}
                </button>
              </div>

              <div className="bg-seryi-bg-dark border border-seryi-bg-light p-3.5 rounded-2xl flex items-center justify-between text-xs">
                <div>
                  <span className="text-[10px] font-bold text-seryi-text uppercase block">Motor de Flujos (n8n Webhook)</span>
                  <span className="font-mono text-seryi-heading font-medium">https://flow.seryiads.com</span>
                </div>
                <button
                  onClick={() => copyToClipboard('https://flow.seryiads.com', 'n8n URL')}
                  className="text-seryi-text hover:text-seryi-green p-1.5 rounded-lg transition-colors"
                >
                  {copiedKey === 'n8n URL' ? <Check size={14} className="text-seryi-green" /> : <Copy size={14} />}
                </button>
              </div>

              <div className="bg-seryi-bg-dark border border-seryi-bg-light p-3.5 rounded-2xl flex items-center justify-between text-xs">
                <div>
                  <span className="text-[10px] font-bold text-seryi-text uppercase block">Despliegues y DevOps</span>
                  <span className="font-mono text-seryi-heading font-medium">Coolify VPS (keylacrm-admin & seryiads)</span>
                </div>
                <span className="text-[10px] font-bold text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-full border border-emerald-500/20">
                  Producción Activa
                </span>
              </div>
            </div>
          </div>

          <div className="bg-indigo-950/20 border border-indigo-900/30 p-4 rounded-2xl flex items-start gap-3">
            <AlertCircle size={18} className="text-indigo-400 shrink-0 mt-0.5" />
            <p className="text-[11px] text-slate-300 leading-relaxed">
              <strong>Tip de Ventas:</strong> Puedes cambiar el correo de cualquier demo en su módulo de configuración para recibir citas y notificaciones reales de tus prospectos durante una llamada, y luego ingresar como Administrador para restablecerlo.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
